import {
  fromObfuscatedUrl,
  getInternalPathFromLocation,
  getRouteTokenPrefix,
  isPublicAppPath,
  isRouteObfuscationEnabled,
  toObfuscatedUrl,
} from "./routeTokens";

/** Normalize login redirect query values to internal app paths. */
export function resolveAppRedirectPath(redirect) {
  if (!redirect || typeof redirect !== "string") return null;
  const trimmed = redirect.trim();
  if (!trimmed) return null;

  if (!isRouteObfuscationEnabled()) {
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  }

  const hash = trimmed.includes("#") ? trimmed.slice(trimmed.indexOf("#")) : "";
  const withoutHash = hash ? trimmed.slice(0, trimmed.indexOf("#")) : trimmed;
  const pathname = withoutHash.split("?")[0];
  const withSlash = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const search = withoutHash.includes("?")
    ? withoutHash.slice(withoutHash.indexOf("?"))
    : "";
  const prefix = getRouteTokenPrefix();

  if (withSlash.startsWith(`${prefix}/`)) {
    const decoded = fromObfuscatedUrl(withSlash);
    if (!decoded) return null;
    return `${decoded.pathname}${decoded.search || ""}${hash}`;
  }

  return `${withSlash}${search}${hash}`;
}

/** Build a browser URL for in-app navigation (sidebar, redirects, navigate()). */
export function toAppPath(path) {
  if (!path || typeof path !== "string") return "/";
  const trimmed = path.trim();
  if (!isRouteObfuscationEnabled()) return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;

  const hash = trimmed.includes("#") ? trimmed.slice(trimmed.indexOf("#")) : "";
  const withoutHash = hash ? trimmed.slice(0, trimmed.indexOf("#")) : trimmed;
  const pathname = withoutHash.split("?")[0];
  const withSlash = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const prefix = getRouteTokenPrefix();

  if (withSlash.startsWith(`${prefix}/`) && withSlash.length > prefix.length + 1) {
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  }

  if (isPublicAppPath(withSlash)) return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return `${toObfuscatedUrl(withoutHash)}${hash}`;
}

/**
 * Query params for the logical app route.
 * Under /du/{token}, params usually live in the token mapping (not location.search).
 * Also merges browser location.search so legacy /du/token?foo=bar URLs keep working.
 */
export function getAppSearchParams(location) {
  const loc = location ?? (typeof window !== "undefined" ? window.location : null);
  if (!loc) return new URLSearchParams();

  const internalPath = getInternalPathFromLocation(loc);
  const qIndex = internalPath.indexOf("?");
  const params = new URLSearchParams(
    qIndex === -1 ? "" : internalPath.slice(qIndex)
  );

  // Preserve any real browser query (e.g. /du/{token}?previous=true bookmarks)
  const browserSearch = loc.search || "";
  if (browserSearch) {
    new URLSearchParams(browserSearch).forEach((value, key) => {
      if (!params.has(key)) params.set(key, value);
    });
  }

  return params;
}

export { isRouteObfuscationEnabled, isPublicAppPath, getInternalPathFromLocation };
